import React from 'react';
import { Shield, Clock, Users, Heart, Brain, Activity, ChevronRight } from 'lucide-react';

const InpatientPrograms = () => {
  const inpatientPrograms = [
    {
      id: 1,
      title: "Mental Health Inpatient Care",
      description: "24/7 supervised care for acute mental health crises requiring intensive treatment and monitoring.",
      duration: "5-14 days typical stay",
      icon: Shield,
      features: ["24/7 medical supervision", "Intensive therapy", "Medication management"]
    },
    {
      id: 2,
      title: "Crisis Stabilization Unit",
      description: "Short-term intensive care for immediate stabilization of acute psychiatric emergencies.",
      duration: "24-72 hours",
      icon: Clock,
      features: ["Rapid intervention", "Safety planning", "Crisis resolution"]
    },
    {
      id: 3,
      title: "Dual Diagnosis Treatment",
      description: "Integrated treatment for co-occurring mental health and substance use disorders.",
      duration: "14-30 days",
      icon: Users,
      features: ["Integrated care", "Substance abuse treatment", "Mental health therapy"]
    },
    {
      id: 4,
      title: "Residential Treatment Program",
      description: "Long-term structured living environment for comprehensive mental health recovery.",
      duration: "30-90 days",
      icon: Heart,
      features: ["Structured environment", "Life skills training", "Aftercare planning"]
    },
    {
      id: 5,
      title: "Psychiatric Intensive Care",
      description: "High-level monitoring and treatment for severe psychiatric conditions requiring constant supervision.",
      duration: "7-21 days",
      icon: Brain,
      features: ["Constant monitoring", "Intensive psychiatric care", "Safety protocols"]
    },
    {
      id: 6,
      title: "Detoxification Program",
      description: "Medical supervision for safe withdrawal from substances with comprehensive support.",
      duration: "5-10 days",
      icon: Activity,
      features: ["Medical detox", "24/7 monitoring", "Withdrawal management"]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-800 via-primary-700 to-primary-600 text-white py-20 lg:py-24">
        <div className="absolute -top-24 left-10 h-72 w-72 rounded-full bg-white/10 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-white/10 blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mb-6">
              Inpatient Programs
            </h1>
            <p className="text-lg text-white/80 max-w-3xl mx-auto">
              Comprehensive residential care for intensive mental health treatment and recovery
            </p>
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="bg-red-600 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center space-x-2">
            <span className="font-semibold">24/7 Crisis Hotline: 988</span>
            <span className="text-red-200">|</span>
            <span>Emergency: 911</span>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {inpatientPrograms.map((program) => (
              <div key={program.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      <program.icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <span className="text-sm text-gray-500">{program.duration}</span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {program.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4">
                    {program.description}
                  </p>
                  
                  <div className="space-y-2 mb-4">
                    {program.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 bg-primary-600 rounded-full"></div>
                        <span className="text-sm text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <button className="w-full bg-primary-600 text-white py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2">
                    <span>Learn More</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Admission Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Admission Process
            </h2>
            <p className="text-xl text-gray-600">
              Simple and confidential admission to get you the care you need
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Initial Assessment
              </h3>
              <p className="text-gray-600">
                Free confidential assessment to determine the appropriate level of care
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Insurance Verification
              </h3>
              <p className="text-gray-600">
                We verify your insurance coverage and explain your benefits
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Admission & Treatment
              </h3>
              <p className="text-gray-600">
                Smooth admission process and immediate start of personalized treatment
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Need Immediate Help?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Our admissions team is available 24/7 to assist you
          </p>
          <button className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
            Call Admissions: 98866 55555
          </button>
        </div>
      </section>
    </div>
  );
};

export default InpatientPrograms;
